import * as React from "react"
import { Box, Button, Grid, Typography } from "@mui/material"
import { CardSeeker } from "@ui-seeker/core"

import {
    CATALOGUE_ROUTES,
    DIGITAL_SHELF_ROUTES,
    INFOCENTER_ROUTES,
    MARKETPLACES_ROUTES,
    PRICING_ROUTES,
    REPRICING_ROUTES,
    STOCK_ROUTES
} from "@constants"
import { customerID, userData } from "@signals"
import useManualLink from "@hooks/useManualLink"
import PageWrapper from "Pages/@PageWrapper"
import { InfoCenterAccordionMenu } from '../@components'
import { contentType } from "../@signals"
import { CONTENT_TEXT, CONTENT_TYPE } from "../constants"
import InfoCenterPageContent from "./Content"


export default function InfoCenterGeneralPage() {
    const isPdfViewerCustomer = [28, 6].includes(customerID.value)
    const { manualLink } = useManualLink(userData.value)

    const props = {
        head: `DIP Insights | ${INFOCENTER_ROUTES.GENERAL_TITLE}`,
        title: INFOCENTER_ROUTES.GENERAL_TITLE,
        subtitle: INFOCENTER_ROUTES.GENERAL_SUBTITLE,
        isEmpty: false,
        actions: ''
    }

    const renderPdfViewer = () => (
        <Grid item xs={12}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    gap: 2,
                    flexWrap: 'wrap'
                }}
            >
                <Box>
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.12em' }}>
                        Info Center
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                        Customer Manual
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        View the documentation in PDF format
                    </Typography>
                </Box>

                {manualLink && (
                    <Button
                        variant="outlined"
                        href={manualLink}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Open PDF
                    </Button>
                )}
            </Box>

            {manualLink ? (
                <Box
                    component="iframe"
                    src={manualLink}
                    title="Customer Manual PDF"
                    sx={{
                        width: '100%',
                        height: '85vh',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.paper'
                    }}
                />
            ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No manual configured for this customer.
                </Typography>
            )}
        </Grid>
    )

    const renderVideoContent = () => {
        const type = contentType.value

        return (
            <>
                <Grid item xs={3} xl={2} sx={{ borderRight: '1px solid #919eab52', paddingTop: '0 !important' }}>
                    <InfoCenterAccordionMenu />
                </Grid>

                <Grid item xs={9} xl={10}>
                    {type === CONTENT_TYPE.PRICING && (
                        <>
                            <InfoCenterPageContent
                                title={PRICING_ROUTES.OVERVIEW_TITLE}
                                video="/videos/pricing_overview_EN.mov"
                                text={PRICING_ROUTES.OVERVIEW_SUBTITLE}
                                sx={{ sxContainer: { borderBottom: '1px solid', borderColor: '#ccc' } }}
                            />
                            <InfoCenterPageContent
                                title={PRICING_ROUTES.CHANGES_TITLE}
                                video="/videos/pricing_changes_EN.mov"
                                text={PRICING_ROUTES.CHANGES_SUBTITLE}
                                sx={{ sxContainer: { mt: 2, borderBottom: '1px solid', borderColor: '#ccc' } }}
                            />
                            <InfoCenterPageContent
                                title={PRICING_ROUTES.MARKET_ANALYZER_TITLE}
                                video="/videos/pricing_market_analyzer_EN.mov"
                                text={PRICING_ROUTES.MARKET_ANALYZER_SUBTITLE}
                                sx={{ sxContainer: { mt: 2 } }}
                            />
                        </>
                    )}

                    {type === CONTENT_TYPE.REPRICING && (
                        <InfoCenterPageContent
                            title={REPRICING_ROUTES.TITLE}
                            video="/videos/tutorial_repricing.mp4"
                            text={CONTENT_TEXT.REPRICING}
                        />
                    )}

                    {type === CONTENT_TYPE.DIGITAL_SHELF && (
                        <InfoCenterPageContent
                            title={DIGITAL_SHELF_ROUTES.TITLE}
                            video="/videos/tutorial_digital_shelf.mp4"
                            text={CONTENT_TEXT.DIGITAL_SHELF}
                        />
                    )}

                    {type === CONTENT_TYPE.CATALOGUE && (
                        <>
                            <InfoCenterPageContent
                                title={CATALOGUE_ROUTES.TITLE}
                                video="/videos/catalogue_analyzer_EN.mov"
                                text={CATALOGUE_ROUTES.SUBTITLE}
                                sx={{ sxContainer: { borderBottom: '1px solid', borderColor: '#ccc' } }}
                            />
                            <InfoCenterPageContent
                                title={CATALOGUE_ROUTES.DETAILS_TITLE}
                                video="/videos/catalogue_details_EN.mov"
                                text={CATALOGUE_ROUTES.DETAILS_SUBTITLE}
                                sx={{ sxContainer: { mt: 2 } }}
                            />
                        </>
                    )}

                    {type === CONTENT_TYPE.MARKETPLACES && (
                        <InfoCenterPageContent
                            title={MARKETPLACES_ROUTES.TITLE}
                            video="/videos/market_analyzer_EN.mov"
                            text={MARKETPLACES_ROUTES.SUBTITLE}
                        />
                    )}

                    {type === CONTENT_TYPE.STOCK && (
                        <>
                            <InfoCenterPageContent
                                title={STOCK_ROUTES.STOCK_ASSORT_TITLE}
                                video="/videos/stock_and_assortment_details_EN.mov"
                                text={STOCK_ROUTES.SUBTITLE}
                                sx={{ sxContainer: { borderBottom: '1px solid', borderColor: '#ccc' } }}
                            />
                            <InfoCenterPageContent
                                title={STOCK_ROUTES.STOCK_CHANGES_TITLE}
                                video="/videos/stock_changes_EN.mov"
                                text={STOCK_ROUTES.STOCK_CHANGES_SUBTITLE}
                                sx={{ sxContainer: { mt: 2 } }}
                            />
                        </>
                    )}

                    {type === CONTENT_TYPE.BASIC_FUNCTIONS && (
                        <InfoCenterPageContent
                            title="Basic Functions"
                            video="/videos/basic_functions_EN.mov"
                            text={CONTENT_TEXT.BASIC_FUNCTIONS}
                        />
                    )}
                </Grid>
            </>
        )
    }

    const cardTitle = isPdfViewerCustomer ? '' : 'Info center - Video content'

    return (
        <PageWrapper {...props}>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <CardSeeker title={cardTitle}>
                        <Grid container spacing={2}>
                            {isPdfViewerCustomer ? renderPdfViewer() : renderVideoContent()}
                        </Grid>
                    </CardSeeker>
                </Grid>
            </Grid>
        </PageWrapper>
    )
}